import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconSearch } from '@tabler/icons-react';
import { Input } from '@core-ui/Components/ui/input';
import { Button } from '@core-ui/Components/ui/button';
import { Badge } from '@core-ui/Components/ui/badge';
import { Avatar, AvatarFallback } from '@core-ui/Components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@core-ui/Components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@core-ui/Components/ui/table';
import { PaginationControls } from '@ui/shared/paginationControls';
import ContactInfoDialog from './ContactInfoDialog';
import WaveMini from '@features/voicemail/components/WaveMini';
import PlayIcon from '@features/voicemail/components/PlayIcon';
import useDashboardApi, { type UseDashboardApiReturn } from '../hooks/useDashboardApi';
import CallPath from './CallPath';
import type { SessionDetailsCall, SessionRecordings, CallRecording, LoadingState, ContactInfo, ContactTimeline } from '../types/dashboard.types';
import { useFeatureFlagsStore } from '@store/featureFlags';
import { Flame } from 'lucide-react';
import { dispositionBadgeStyle, NOT_CONNECTED } from '../constants/dispositionColors';

interface CallsTableProps {
  calls: SessionDetailsCall[];
  /** Needed to fetch a row's call path when it is expanded. */
  sessionId: string;
  /**
   * A new page of rows is on its way. Dims the rows in place instead of
   * unmounting the page for a skeleton, so the filters keep their focus and the
   * layout does not jump.
   */
  isRefreshing?: boolean;
  searchQuery: string;
  dispositionFilter: string;
  agentFilter: string;
  // Pagination is server-side; the parent owns this state.
  currentPage: number;
  setCurrentPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rows: number) => void;
  totalCalls: number;
  totalPages: number;
  actionFilter: string;
  uniqueDispositions: string[];
  uniqueAgents: Array<{ id: string; name: string }>;
  uniqueActions: string[];
  onSearchChange: (query: string) => void;
  onDispositionChange: (disposition: string) => void;
  onAgentChange: (agent: string) => void;
  onActionChange: (action: string) => void;
  onLoadRecording: (callId: string, contactId?: string) => Promise<void>;
  onCloseRecording: () => void;
  selectedRecordings: SessionRecordings | null;
  recordingsLoadingState: LoadingState;
}

function getDispositionLabel (key: string, t: (k: string, fallback: string) => string): string {
  return t(`dashboard.dispositions.${key}`, key);
}

/** Dispositions that mean the SDR never gave the call an outcome. */
const UNCLASSIFIED_DISPOSITIONS = new Set(['default_action', 'DA', '']);

/**
 * The disposition to SHOW for a row, which is not always the one stored.
 *
 * A dial that never connected is stored as unclassified, so the row read
 * "Default Action" — indistinguishable from a call that happened and simply
 * was not classified. It looked like missing data rather than like an outcome,
 * and next to a second row for the same contact that DID complete, it looked
 * like the dashboard was double-counting.
 *
 * The action is what separates the two, and it has to be part of the rule:
 * measured over the 240 unclassified non-terminal callLogs since May 2026,
 * 202 never got past `called`, but 29 reached `answered` and 8 reached
 * `transferred`. Those 37 calls DID connect — the SDR just never classified
 * them — so calling them "not connected" would be false.
 *
 * Display only. The stored value is untouched, so the counts, the disposition
 * breakdown and the filter all keep working on the real data.
 */
function resolveDisposition (disposition: string, action: string): string {
  const isUnclassified = UNCLASSIFIED_DISPOSITIONS.has(disposition);

  return isUnclassified && action === 'called' ? NOT_CONNECTED : disposition;
}


function getActionLabel (key: string, t: (k: string, fallback: string) => string): string {
  return t(`dashboard.actions.${key}`, key);
}

const ACTION_COLORS: Record<string, string> = {
  called: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  transferred: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  answered: 'bg-green-500/20 text-green-400 border-green-500/30',
  voicemail: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  not_answered: 'bg-red-500/20 text-red-400 border-red-500/30',
  call_ended: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  cut_off: 'bg-red-500/20 text-red-400 border-red-500/30',
  transfer_error: 'bg-red-500/20 text-red-400 border-red-500/30',
};

function getInitials (name: string): string {
  if (name === '' || name === null || name === undefined) {
    return '??';
  }
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

function formatDate (
  dateValue: string | { $date: string } | null | undefined,
  lang: string
): string {
  if (dateValue == null) {
    return '-';
  }

  const localeLower = (lang ?? '').toLowerCase();
  const locale =
      localeLower.startsWith('es') ? 'es-ES' :
        localeLower.startsWith('en') ? 'en-US' :
          'en-US';

  let dateString: string;
  if (typeof dateValue === 'object' && dateValue.$date !== '') {
    dateString = dateValue.$date;
  } else if (typeof dateValue === 'string') {
    dateString = dateValue;
  } else {
    return '-';
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return '-';
  }

  const dateStr = date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${timeStr}, ${dateStr}`;
}

// Speed options for playback
const PLAYBACK_SPEEDS = [1, 1.5, 2] as const;
type PlaybackSpeed = typeof PLAYBACK_SPEEDS[number];

function parseDuration (duration?: string): number {
  if (duration == null || duration === '') {
    return 0;
  }
  const parsed = parseFloat(duration);
  return isNaN(parsed) ? 0 : parsed;
}

// Inline Recording Player
function InlineRecordingPlayer ({
  recordings,
  isLoading,
  isError,
  onClose,
  onRetry,
}: {
  recordings: SessionRecordings | null;
  isLoading: boolean;
  isError: boolean;
  onClose: () => void;
  onRetry: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeRecording, setActiveRecording] = useState<'sdr' | 'agent'>(
    recordings?.sdrCallRecording != null ? 'sdr' : 'agent'
  );
  const [playbackSpeed, setPlaybackSpeed] = useState<PlaybackSpeed>(1);
  const [audioError, setAudioError] = useState(false);

  const currentRecording: CallRecording | undefined = activeRecording === 'sdr' ? recordings?.sdrCallRecording : recordings?.agentCallRecording;
  const recordingUrl = currentRecording?.recordingUrl;

  const [duration, setDuration] = useState(() => parseDuration(currentRecording?.duration));

  // Reset active recording when recordings change
  useEffect(() => {
    setActiveRecording(recordings?.sdrCallRecording != null ? 'sdr' : 'agent');
  }, [recordings]);

  // Update duration when the active recording changes
  useEffect(() => {
    setDuration(parseDuration(currentRecording?.duration));
  }, [currentRecording?.duration]);

  useEffect(() => {
    if (recordingUrl == null) {
      return;
    }

    setAudioError(false);
    const audio = new Audio(recordingUrl);
    audioRef.current = audio;
    audio.playbackRate = playbackSpeed;

    const onLoadedMetadata = (): void => {
      setDuration(audio.duration);
    };
    const onTimeUpdate = (): void => {
      if (audio.duration > 0) {
        setProgress(audio.currentTime / audio.duration);
      }
    };
    const onEnded = (): void => {
      setIsPlaying(false);
      setProgress(0);
    };
    const onError = (): void => {
      setAudioError(true);
      setIsPlaying(false);
    };

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio.pause();
      audio.src = '';
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingUrl]);

  const togglePlay = (): void => {
    if (audioRef.current == null) {
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {
        setAudioError(true);
        setIsPlaying(false);
      });
    }
  };

  const cycleSpeed = (): void => {
    const currentIndex = PLAYBACK_SPEEDS.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % PLAYBACK_SPEEDS.length;
    const newSpeed = PLAYBACK_SPEEDS[nextIndex];
    setPlaybackSpeed(newSpeed);
    if (audioRef.current != null) {
      audioRef.current.playbackRate = newSpeed;
    }
  };

  const handleDownload = (): void => {
    if (recordingUrl == null) {
      return;
    }
    window.open(recordingUrl, '_blank');
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentTime = progress * duration;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="text-xs">{t('dashboard.callsTable.loading')}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-red-500">{t('dashboard.callsTable.error')}</span>
        <Button
          variant="outline"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={onRetry}
        >
          {t('dashboard.callsTable.retry')}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-foreground"
          onClick={onClose}
          title={t('dashboard.callsTable.close')}
        >
          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </Button>
      </div>
    );
  }

  if (audioError) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-red-500">{t('dashboard.callsTable.audioUnavailable')}</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-foreground"
          onClick={onClose}
          title={t('dashboard.callsTable.close')}
        >
          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </Button>
      </div>
    );
  }

  if (recordings == null || (recordings.sdrCallRecording == null && recordings.agentCallRecording == null)) {
    return <span className="text-muted-foreground text-sm">{t('dashboard.callsTable.noRecording')}</span>;
  }

  const hasBoth = recordings.sdrCallRecording != null && recordings.agentCallRecording != null;

  return (
    <div className="flex items-center gap-2">
      {/* Play/Pause */}
      <Button
        variant="ghost"
        size="icon"
        className={`h-9 w-9 rounded-full border ${isPlaying ? 'bg-sky-100 text-sky-700 border-sky-300' : 'text-primary hover:text-primary/80 border-border'}`}
        onClick={togglePlay}
        title={isPlaying ? t('dashboard.callsTable.pause') : t('dashboard.callsTable.play')}
      >
        <PlayIcon playing={isPlaying} />
      </Button>

      {/* Waveform */}
      <WaveMini progress={progress} animated={isPlaying} />

      {/* Current Time / Total Duration */}
      <span className="text-xs text-muted-foreground min-w-[70px] tabular-nums">
        {duration > 0 ? `${formatDuration(currentTime)} / ${formatDuration(duration)}` : '--:--'}
      </span>

      {/* Speed Control */}
      <Button
        variant="outline"
        size="sm"
        className="h-6 px-2 text-xs min-w-[40px] font-medium"
        onClick={cycleSpeed}
        title={t('dashboard.callsTable.changeSpeed')}
      >
        {playbackSpeed}x
      </Button>

      {/* Recording Type Toggle (SDR/Agent) */}
      {hasBoth ? (
        <div className="flex h-7 rounded-md border border-border overflow-hidden">
          <button
            type="button"
            className={`px-2 text-xs font-medium transition-colors ${activeRecording === 'sdr' ? 'bg-sky-100 text-sky-700' : 'text-muted-foreground hover:bg-muted/60'}`}
            onClick={() => {
              setActiveRecording('sdr');
              setIsPlaying(false);
              setProgress(0);
            }}
          >
            {t('dashboard.callsTable.sdr')}
          </button>
          <button
            type="button"
            className={`px-2 text-xs font-medium border-l border-border transition-colors ${activeRecording === 'agent' ? 'bg-sky-100 text-sky-700' : 'text-muted-foreground hover:bg-muted/60'}`}
            onClick={() => {
              setActiveRecording('agent');
              setIsPlaying(false);
              setProgress(0);
            }}
          >
            {t('dashboard.callsTable.agent')}
          </button>
        </div>
      ) : (
        <span className="text-xs text-muted-foreground">
          {recordings.sdrCallRecording != null ? t('dashboard.callsTable.sdr') : t('dashboard.callsTable.agent')}
        </span>
      )}

      {/* Download Button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground hover:text-foreground"
        onClick={handleDownload}
        title={t('dashboard.callsTable.downloadRecording')}
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
          <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
        </svg>
      </Button>

      {/* Close Button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 text-muted-foreground hover:text-foreground"
        onClick={onClose}
        title={t('dashboard.callsTable.closePlayer')}
      >
        <svg viewBox="0 0 24 24" className="h-3 w-3" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
        </svg>
      </Button>
    </div>
  );
}

export default function CallsTable ({
  calls,
  sessionId,
  isRefreshing = false,
  searchQuery,
  dispositionFilter,
  agentFilter,
  actionFilter,
  currentPage,
  setCurrentPage,
  rowsPerPage,
  setRowsPerPage,
  totalCalls,
  uniqueDispositions,
  uniqueAgents,
  uniqueActions,
  onSearchChange,
  onDispositionChange,
  onAgentChange,
  onActionChange,
  onLoadRecording,
  onCloseRecording,
  selectedRecordings,
  recordingsLoadingState,
}: CallsTableProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [activeCallId, setActiveCallId] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] = useState<ContactInfo | null>(null);
  const [contactLoadingState, setContactLoadingState] = useState<LoadingState>('IDLE');
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<ContactTimeline | null>(null);
  const [timelineState, setTimelineState] = useState<LoadingState>('IDLE');
  const timelineCache = useRef<Map<string, ContactTimeline>>(new Map());

  const {
    getContactByIdAsync,
    getContactTimelineAsync,
  }: UseDashboardApiReturn = useDashboardApi();
  const featureFlags = useFeatureFlagsStore((s) => s.flags);

  // `calls` is already the current page: the server filters, sorts and pages.
  // This component used to receive every call of the session and slice it here,
  // which meant the browser held the whole session to render ten rows.
  const paginatedCalls = calls;

  /**
   * Expanding a row loads its call path. Collapsing is free — the row is the
   * only toggle, and everything interactive inside a cell stops propagation so
   * playing a recording or opening a contact never closes the panel.
   */
  const handleRowClick = async (callId: string, contactId: string): Promise<void> => {
    if (expandedCallId === callId) {
      setExpandedCallId(null);
      return;
    }

    setExpandedCallId(callId);

    // A finished session's events never change, so opening the same row twice
    // must not hit the network twice. The cache lives for the life of the page.
    // Keyed by callId, not contactId: a contact dialled twice has two rows and
    // each one now carries a different path.
    const cached = timelineCache.current.get(callId);

    if (cached !== undefined) {
      setTimeline(cached);
      setTimelineState('SUCCESS');
      return;
    }

    setTimeline(null);
    setTimelineState('LOADING');

    const response = await getContactTimelineAsync(sessionId, contactId, callId);

    if (response.success && response.data !== undefined) {
      timelineCache.current.set(callId, response.data);
      setTimeline(response.data);
      setTimelineState('SUCCESS');
      return;
    }

    setTimelineState('ERROR');
  };

  const handlePlayClick = async (callId: string, contactId?: string): Promise<void> => {
    if (activeCallId === callId) {
      // If clicking the same call, just toggle off
      setActiveCallId(null);
      return;
    }
    setActiveCallId(callId);
    await onLoadRecording(callId, contactId);
  };

  const handleCloseRecording = (): void => {
    setActiveCallId(null);
    onCloseRecording();
  };

  const handleContactClick = async (contactId: string): Promise<void> => {
    setIsContactDialogOpen(true);
    setContactLoadingState('LOADING');
    setSelectedContact(null);

    const response = await getContactByIdAsync(contactId);

    if (response.success) {
      setSelectedContact(response.data);
      setContactLoadingState('SUCCESS');
    } else {
      setContactLoadingState('ERROR');
    }
  };

  const hasActiveFilters =
    searchQuery !== '' ||
    dispositionFilter !== 'all' ||
    (uniqueActions.length > 0 && actionFilter !== 'all') ||
    (uniqueAgents.length > 0 && agentFilter !== 'all');

  const clearFilters = (): void => {
    onSearchChange('');
    onDispositionChange('all');
    onActionChange('all');
    onAgentChange('all');
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border/60">
        <h3 className="font-semibold text-foreground">{t('dashboard.callsTable.title')}</h3>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 p-4 border-b border-border/60">
        <div className="relative flex-1 max-w-sm">
          <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-qa-id="search-bar"
            value={searchQuery}
            onChange={(e) => {
              onSearchChange(e.target.value);
            }}
            placeholder={t('dashboard.callsTable.searchPlaceholder')}
            className="pl-10 h-9 bg-background"
          />
        </div>

        <Select value={dispositionFilter} onValueChange={onDispositionChange}>
          <SelectTrigger data-qa-id="dispositions-button" className="w-[180px] h-9 bg-background">
            <SelectValue placeholder={t('dashboard.callsTable.allDispositions')} />
          </SelectTrigger>
          <SelectContent className="border-border">
            <SelectItem value="all">{t('dashboard.callsTable.allDispositions')}</SelectItem>
            {uniqueDispositions.map((d) => (
              <SelectItem data-qa-id={getDispositionLabel(d, t)} key={d} value={d} >
                {getDispositionLabel(d, t)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {uniqueActions.length > 0 && (
          <Select value={actionFilter} onValueChange={onActionChange}>
            <SelectTrigger data-qa-id="actions-button" className="w-[160px] h-9 bg-background">
              <SelectValue placeholder={t('dashboard.callsTable.allActions')} />
            </SelectTrigger>
            <SelectContent className="border-border">
              <SelectItem value="all">{t('dashboard.callsTable.allActions')}</SelectItem>
              {uniqueActions.map((action) => (
                <SelectItem data-qa-id={getActionLabel(action, t)} key={action} value={action}>
                  {getActionLabel(action, t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {uniqueAgents.length > 0 && (
          <Select value={agentFilter} onValueChange={onAgentChange}>
            <SelectTrigger data-qa-id="agents-button" className="w-[160px] h-9 bg-background">
              <SelectValue placeholder={t('dashboard.callsTable.allAgents')} />
            </SelectTrigger>
            <SelectContent className="border-border">
              <SelectItem value="all">{t('dashboard.callsTable.allAgents')}</SelectItem>
              {uniqueAgents.map((agent) => (
                <SelectItem data-qa-id={agent.name} key={agent.id} value={agent.id}>
                  {agent.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Same button as the sessions FilterBar: always present, disabled
            until a filter is on, so the bar does not jump when it appears. */}
        <Button
          data-qa-id="calls-clear-all-button"
          variant="ghost"
          size="sm"
          disabled={!hasActiveFilters}
          onClick={clearFilters}
          className="text-red-500 hover:text-red-600 h-9 disabled:opacity-50"
        >
          {t('dashboard.filterBar.clearAll')}
        </Button>
      </div>

      {/* Table. Only the rows dim while a new page loads — the filter controls
          above stay fully interactive so focus and typing are never lost. */}
      <div
        className={`overflow-auto transition-opacity duration-200 ${
          isRefreshing ? 'opacity-50' : 'opacity-100'
        }`}
        aria-busy={isRefreshing}
      >
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow className="border-b border-border/60 hover:bg-transparent">
              <TableHead data-qa-id="contact-header" className="text-muted-foreground font-medium">{t('dashboard.callsTable.contact')}</TableHead>
              <TableHead className="text-muted-foreground font-medium">{t('dashboard.callsTable.disposition')}</TableHead>
              <TableHead className="text-muted-foreground font-medium">{t('dashboard.callsTable.action')}</TableHead>
              {uniqueAgents.length > 0 && (
                <TableHead className="text-muted-foreground font-medium">{t('dashboard.callsTable.agent')}</TableHead>
              )}
              {featureFlags.callRecordings && (
                <TableHead data-qa-id="recordings-header" className="text-muted-foreground font-medium">{t('dashboard.callsTable.recording')}</TableHead>
              )}
              <TableHead className="text-muted-foreground font-medium">{t('dashboard.callsTable.date')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedCalls.length === 0 ? (
              <TableRow data-qa-id="call-row-0">
                <TableCell
                  data-qa-id="call-not-found"
                  colSpan={uniqueAgents.length > 0 ? 6 : 5}
                  className="text-center py-12 text-muted-foreground"
                >
                  {t('dashboard.callsTable.noCallsFound')}
                </TableCell>
              </TableRow>
            ) : (
              paginatedCalls.map((call, r) => (
                <React.Fragment key={call.callId}>
                  <TableRow
                    data-qa-id={`call-row-${r}`}
                    className="border-b border-border/60 hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => {
                      void handleRowClick(call.callId, call.contactId);
                    }}
                  >
                    {/* Contact */}
                    <TableCell data-qa-id={`contact-cell-${r}`} className="py-3">
                      <button
                        type="button"
                        className="flex items-center gap-3 hover:opacity-80 transition-opacity text-left"
                        // Opening the contact must not also toggle the row.
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleContactClick(call.contactId);
                        }}
                      >
                        <Avatar className="h-9 w-9 shadow">
                          <AvatarFallback className="bg-primary/20 text-primary text-xs">
                            {getInitials(call.contactName)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-primary hover:underline cursor-pointer">
                          {call.contactName !== '' ? call.contactName : t('dashboard.callsTable.unknownContact')}
                        </span>
                        {call.flagged && (
                          <Flame className="size-4 md:size-5 shrink-0 text-amber-500/40 fill-current" />
                        )}
                      </button>
                    </TableCell>

                    {/* Disposition */}
                    <TableCell data-qa-id={`disposition-cell-${r}`}>
                      {call.disposition != null ? (
                        (() => {
                          const shown = resolveDisposition(call.disposition, call.action);

                          return (
                            <Badge
                              variant="outline"
                              className="text-xs px-2 py-0.5 border"
                              style={dispositionBadgeStyle(shown)}
                              title={
                                shown === NOT_CONNECTED
                                  ? t(
                                    'dashboard.dispositions.notConnectedHelp',
                                    'The call was dialled but never connected, so there is no outcome to classify. It still counts as a call.',
                                  )
                                  : undefined
                              }
                            >
                              {getDispositionLabel(shown, t)}
                            </Badge>
                          );
                        })()
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>

                    {/* Action */}
                    <TableCell data-qa-id={`action-cell-${r}`}>
                      <Badge
                        variant="outline"
                        className={`text-xs px-2 py-0.5 ${
                          ACTION_COLORS[call.action] ?? 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                        }`}
                      >
                        {getActionLabel(call.action, t)}
                      </Badge>
                    </TableCell>

                    {/* Agent (if available) */}
                    {uniqueAgents.length > 0 && (
                      <TableCell data-qa-id={`agent-cell-${r}`} className="text-muted-foreground">
                        {call.agentName ?? '-'}
                      </TableCell>
                    )}

                    {/* Recording. The whole cell stops propagation: the player
                      has several controls, and any of them bubbling up would
                      collapse the row you are listening from. */}
                    {featureFlags.callRecordings && (
                      <TableCell onClick={(e) => {
                        e.stopPropagation(); 
                      }}>
                        {call.existRecordings ? (
                          activeCallId === call.callId ? (
                            <InlineRecordingPlayer
                              key={call.callId}
                              recordings={selectedRecordings}
                              isLoading={recordingsLoadingState === 'LOADING'}
                              isError={recordingsLoadingState === 'ERROR'}
                              onClose={handleCloseRecording}
                              onRetry={() => {
                                void onLoadRecording(call.callId, call.contactId);
                              }}
                            />
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 gap-2 text-primary hover:text-primary/80"
                              onClick={() => {
                                void handlePlayClick(call.callId, call.contactId);
                              }}
                            >
                              <PlayIcon playing={false} />
                              <span className="text-xs">{t('dashboard.callsTable.play')}</span>
                            </Button>
                          )
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    )}

                    {/* Date */}
                    <TableCell className="text-muted-foreground">
                      {formatDate(call.createdAt, i18n.language)}
                    </TableCell>
                  </TableRow>
                  {expandedCallId === call.callId && (
                    <TableRow className="border-b border-border/60 hover:bg-transparent">
                      <TableCell
                        colSpan={uniqueAgents.length > 0 ? 6 : 5}
                        className="bg-muted/20 px-5 py-3"
                      >
                        <CallPath
                          timeline={timeline}
                          isLoading={timelineState === 'LOADING'}
                          isError={timelineState === 'ERROR'}
                          shownAction={call.action}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <PaginationControls
        mode="server"
        totalItems={totalCalls}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={(rows) => {
          setRowsPerPage(rows);
          setCurrentPage(1);
        }}
        rowsPerPageOptions={[10, 20, 50]}
        className="border-t border-border/60"
      />

      {/* Contact Info Dialog */}
      <ContactInfoDialog
        contact={selectedContact}
        open={isContactDialogOpen}
        onOpenChange={setIsContactDialogOpen}
        loadingState={contactLoadingState}
      />
    </div>
  );
}
